import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  ArrowUpRight,
  Code,
  Cloud,
  Smartphone,
  Shield,
  Settings,
  Building,
  Zap,
  Store,
} from "lucide-react";
import "./ModernHeader.css";

interface NavLeaf {
  title: string;
  href: string;
  description?: string;
  icon?: React.ReactNode;
}

interface NavColumn {
  title: string;
  items: NavLeaf[];
}

interface NavigationItem {
  title: string;
  type: "link" | "mega";
  href?: string;
  path?: string;
  columns?: NavColumn[];
}

const navigationData: NavigationItem[] = [
  { title: "Home", type: "link", href: "/", path: "/" },
  {
    title: "Our Products",
    type: "mega",
    columns: [
      {
        title: "Development",
        items: [
          {
            title: "Web Development",
            href: "/software-dev",
            description: "Custom web applications and solutions",
            icon: <Code size={17} strokeWidth={1.5} />,
          },
          {
            title: "Mobile Applications",
            href: "/mobile-apps",
            description: "iOS and Android app development",
            icon: <Smartphone size={17} strokeWidth={1.5} />,
          },
        ],
      },
      {
        title: "Enterprise Solutions",
        items: [
          {
            title: "Microsoft Solutions",
            href: "/microsoft-solutions",
            description: "Microsoft 365 and Azure services",
            icon: <Cloud size={17} strokeWidth={1.5} />,
          },
          {
            title: "Load Balancing",
            href: "/edge-nexus",
            description: "EdgeNexus load balancing solutions",
            icon: <Settings size={17} strokeWidth={1.5} />,
          },
          {
            title: "Fiscal Solutions",
            href: "/fiscal-services",
            description: "Financial management systems",
            icon: <Shield size={17} strokeWidth={1.5} />,
          },
          {
            title: "OmniMart",
            href: "/omni-mart",
            description: "Enterprise retail management platform",
            icon: <Store size={17} strokeWidth={1.5} />,
          },
        ],
      },
      {
        title: "Specialized",
        items: [
          {
            title: "Automated Form Processing",
            href: "/power-pages",
            description: "Microsoft Power Platform automations",
            icon: <Zap size={17} strokeWidth={1.5} />,
          },
          {
            title: "GSC Guest House",
            href: "/gsc-guest-house",
            description: "Guest house management system",
            icon: <Building size={17} strokeWidth={1.5} />,
          },
          {
            title: "eGRC Solutions",
            href: "/egrc-solutions",
            description: "Enterprise governance and compliance",
            icon: <Shield size={17} strokeWidth={1.5} />,
          },
        ],
      },
    ],
  },
  { title: "Journal", type: "link", href: "/blog-posts", path: "/blog-posts" },
  { title: "Our Team", type: "link", href: "/team", path: "/team" },
  { title: "Contact", type: "link", href: "/contact", path: "/contact" },
];

const ModernHeader: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [openMega, setOpenMega] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Condense the bar and drive the reading filament along its base edge.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const span =
          document.documentElement.scrollHeight - window.innerHeight || 1;
        setScrolled(y > 24);
        setProgress(Math.min(1, Math.max(0, y / span)));
        frame = 0;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Any navigation closes whatever was open.
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenMega(null);
  }, [location.pathname]);

  // Lock the page behind the drawer without losing the scroll position.
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpenMega(null);
      setMobileMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeSection = (() => {
    const path = location.pathname;
    const inProducts = navigationData
      .find((item) => item.type === "mega")
      ?.columns?.some((col) => col.items.some((leaf) => leaf.href === path));
    if (inProducts) return "Our Products";
    return navigationData.find((item) => item.path === path)?.title ?? "";
  })();

  // A short grace period lets the pointer cross the gap into the panel.
  const holdOpen = useCallback((title: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMega(title);
  }, []);

  const releaseOpen = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMega(null), 140);
  }, []);

  return (
    <header
      className={`dis-nav ${scrolled ? "is-scrolled" : ""} ${
        openMega ? "is-open" : ""
      }`}
    >
      <div className="dis-nav__bar">
        <Link
          to="/"
          className="dis-nav__logo"
          aria-label="Diamond Information Systems — home"
        >
          <img src="/images/main-logo-mark.png" alt="Diamond Information Systems" />
        </Link>

        <nav className="dis-nav__desk" aria-label="Primary">
          {navigationData.map((item) =>
            item.type === "link" ? (
              <Link
                key={item.title}
                to={item.href as string}
                className={`dis-nav__item ${
                  activeSection === item.title ? "is-active" : ""
                }`}
              >
                {item.title}
              </Link>
            ) : (
              <div
                key={item.title}
                className="dis-nav__slot"
                onMouseEnter={() => holdOpen(item.title)}
                onMouseLeave={releaseOpen}
              >
                <button
                  type="button"
                  className={`dis-nav__item ${
                    activeSection === item.title ? "is-active" : ""
                  } ${openMega === item.title ? "is-lit" : ""}`}
                  aria-expanded={openMega === item.title}
                  onClick={() =>
                    setOpenMega(openMega === item.title ? null : item.title)
                  }
                >
                  {item.title}
                  <ChevronDown size={13} strokeWidth={1.5} aria-hidden="true" />
                </button>
              </div>
            ),
          )}
        </nav>

        <div className="dis-nav__end">
          <Link to="/admin-login" className="dis-nav__admin">
            Admin
          </Link>
          <Link to="/contact" className="dis-nav__cta">
            Work with us
            <ArrowUpRight size={14} strokeWidth={1.5} aria-hidden="true" />
          </Link>
          <button
            type="button"
            className="dis-nav__burger"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X size={20} strokeWidth={1.5} />
            ) : (
              <Menu size={20} strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Reading filament — the bar's own instrument readout. */}
        <span
          className="dis-nav__progress"
          style={{ transform: `scaleX(${progress})` }}
          aria-hidden="true"
        />
      </div>

      {/* Mega panel: one shared surface that unmasks downward. */}
      {navigationData
        .filter((item) => item.type === "mega")
        .map((item) => (
          <div
            key={item.title}
            className={`dis-mega ${openMega === item.title ? "is-in" : ""}`}
            onMouseEnter={() => holdOpen(item.title)}
            onMouseLeave={releaseOpen}
            aria-hidden={openMega !== item.title}
          >
            <div className="dis-mega__inner">
              {item.columns?.map((col) => (
                <div key={col.title} className="dis-mega__col">
                  <h3 className="dis-mega__col-title">{col.title}</h3>
                  <div className="dis-mega__links">
                    {col.items.map((leaf) => (
                      <Link
                        key={leaf.title}
                        to={leaf.href}
                        className="dis-mega__link"
                        tabIndex={openMega === item.title ? 0 : -1}
                        onClick={() => setOpenMega(null)}
                      >
                        <span className="dis-mega__icon">{leaf.icon}</span>
                        <span className="dis-mega__copy">
                          <span className="dis-mega__name">{leaf.title}</span>
                          {leaf.description ? (
                            <span className="dis-mega__desc">
                              {leaf.description}
                            </span>
                          ) : null}
                        </span>
                        <ArrowUpRight
                          size={14}
                          strokeWidth={1.5}
                          className="dis-mega__arrow"
                          aria-hidden="true"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {/* Mobile drawer */}
      <div
        className={`dis-drawer__veil ${isMobileMenuOpen ? "is-in" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`dis-drawer ${isMobileMenuOpen ? "is-in" : ""}`}
        role="dialog"
        aria-modal={isMobileMenuOpen}
        aria-label="Menu"
      >
        <div className="dis-drawer__scroll">
          <nav className="dis-drawer__nav">
            {navigationData.map((item, i) =>
              item.type === "link" ? (
                <Link
                  key={item.title}
                  to={item.href as string}
                  className="dis-drawer__row"
                  style={{ "--i": i } as React.CSSProperties}
                >
                  <span className="dis-drawer__num">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item.title}
                </Link>
              ) : (
                <div
                  key={item.title}
                  className="dis-drawer__group"
                  style={{ "--i": i } as React.CSSProperties}
                >
                  <button
                    type="button"
                    className={`dis-drawer__row dis-drawer__row--btn ${
                      expanded[item.title] ? "is-open" : ""
                    }`}
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [item.title]: !prev[item.title],
                      }))
                    }
                    aria-expanded={!!expanded[item.title]}
                  >
                    <span className="dis-drawer__num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {item.title}
                    <ChevronDown
                      size={18}
                      strokeWidth={1.5}
                      className="dis-drawer__chev"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    className={`dis-drawer__sub ${
                      expanded[item.title] ? "is-open" : ""
                    }`}
                  >
                    <div className="dis-drawer__sub-inner">
                      {item.columns?.map((col) => (
                        <div key={col.title} className="dis-drawer__sub-col">
                          <h4 className="dis-drawer__sub-title">{col.title}</h4>
                          {col.items.map((leaf) => (
                            <Link
                              key={leaf.title}
                              to={leaf.href}
                              className="dis-drawer__sub-link"
                            >
                              {leaf.icon}
                              {leaf.title}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ),
            )}
          </nav>

          <div className="dis-drawer__foot">
            <Link
              to="/contact"
              className="dis-btn dis-btn--light dis-btn--block"
            >
              Work with us
            </Link>
            <Link to="/admin-login" className="dis-drawer__admin">
              Admin login
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
